import Icon from "@ch/components/Icons/Icon";
import { type FC, useEffect, useState } from "react";
import type { KemCitationResource } from "../utils/types";

export interface KemCitationPopoverProps {
  isOpen: boolean;
  resources: KemCitationResource[];
}

const KemCitationPopover: FC<KemCitationPopoverProps> = ({
  isOpen,
  resources,
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const total = resources.length;
  const resource = resources[pageIndex];

  useEffect(() => {
    if (!isOpen) {
      setPageIndex(0);
    }
  }, [isOpen]);

  if (!resource) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {total > 1 ? (
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <div className="flex items-center gap-1">
            <button
              aria-label="Previous citation"
              className="rounded p-0.5 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={pageIndex === 0}
              onClick={() => {
                setPageIndex((current) => Math.max(0, current - 1));
              }}
              type="button"
            >
              <Icon color="#000" name="chevron-left" size={16} />
            </button>
            <span className="min-w-[2.5rem] text-center text-xs">
              {pageIndex + 1}/{total}
            </span>
            <button
              aria-label="Next citation"
              className="rounded p-0.5 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={pageIndex >= total - 1}
              onClick={() => {
                setPageIndex((current) => Math.min(total - 1, current + 1));
              }}
              type="button"
            >
              <Icon color="#000" name="chevron-right" size={16} />
            </button>
          </div>
          <span className="text-xs">
            {total} {total === 1 ? "source" : "sources"}
          </span>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-col gap-2">
        <p className="text-gray-900 text-sm font-semibold capitalize">
          {resource.label}
        </p>
        <a
          className="break-all text-sm text-blue-600 underline"
          href={resource.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          {resource.url}
        </a>
      </div>
    </div>
  );
};

export default KemCitationPopover;
